import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { formatAppDate } from '../../utils/dateUtils';

const STATUS_OPTIONS = ['ALL', 'AVAILABLE', 'RESERVED', 'QUARANTINE', 'DEPLETED', 'EXPIRED'];

const STATUS_STYLES = {
  AVAILABLE: { className: 'status-success', color: '#28a745', background: '#e6f4ea' },
  RESERVED: { className: 'status-warning', color: '#856404', background: '#fff3cd' },
  QUARANTINE: { className: '', color: '#e67e22', background: '#fef5e7' },
  DEPLETED: { className: 'status-inactive', color: '#6c757d', background: '#e9ecef' },
  EXPIRED: { className: 'status-error', color: '#dc3545', background: '#fbe9e7' }
};

const ItemLotsTab = ({ itemId }) => {
  const { t } = useTranslation('procurement');

  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedLot, setSelectedLot] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (itemId) {
      fetchLots();
    }
  }, [itemId, statusFilter]);

  const fetchLots = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `/api/inventory/${itemId}/lots`;
      const params = [];
      if (statusFilter && statusFilter !== 'ALL') {
        params.push(`status=${statusFilter}`);
      }
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
      const response = await apiFetch(getApiUrl(url));
      if (!response.ok) {
        throw new Error(`Failed to fetch lots: ${response.status}`);
      }
      const data = await response.json();
      setLots(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching lots:', err);
      setError(err.message);
      setLots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (lot) => {
    setDetailLoading(true);
    setEditMode(false);
    try {
      const response = await apiFetch(getApiUrl(`/api/inventory/lots/${lot.lot_id}`));
      if (!response.ok) {
        throw new Error(`Failed to fetch lot detail: ${response.status}`);
      }
      const data = await response.json();
      setSelectedLot(data);
      setEditData({
        location: data.location || '',
        notes: data.notes || '',
        expiry_date: data.expiry_date ? data.expiry_date.substring(0, 10) : '',
        supplier_batch_number: data.supplier_batch_number || ''
      });
    } catch (err) {
      console.error('Error fetching lot detail:', err);
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedLot(null);
    setEditMode(false);
    setEditData({});
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!selectedLot) return;
    setSaving(true);
    try {
      const body = {
        location: editData.location || null,
        notes: editData.notes || null,
        expiry_date: editData.expiry_date || null,
        supplier_batch_number: editData.supplier_batch_number || null,
        status: selectedLot.status
      };
      const response = await apiFetch(getApiUrl(`/api/inventory/lots/${selectedLot.lot_id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        throw new Error(`Failed to update lot: ${response.status}`);
      }
      const updated = await response.json();
      setSelectedLot(updated);
      setEditMode(false);
      fetchLots();
    } catch (err) {
      console.error('Error updating lot:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatQuantity = (value) => {
    if (value == null) return '0.000';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    });
  };

  const formatCost = (value) => {
    if (value == null || parseFloat(value) === 0) return '-';
    return `$${parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatAppDate(dateStr);
  };

  const renderStatusBadge = (status) => {
    const style = STATUS_STYLES[status] || { color: '#333', background: '#f0f0f0' };
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '0.8em',
          fontWeight: 600,
          color: style.color,
          backgroundColor: style.background
        }}
      >
        {status}
      </span>
    );
  };

  const renderFilters = () => (
    <div className="form-row" style={{ marginBottom: '16px', alignItems: 'flex-end' }}>
      <div className="form-group" style={{ minWidth: '180px' }}>
        <label>{t('materials.lots.filterByStatus', 'Filter by Status')}</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_OPTIONS.map(status => (
            <option key={status} value={status}>
              {status === 'ALL'
                ? t('materials.lots.allStatuses', 'All Statuses')
                : status}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderTable = () => {
    if (loading) {
      return <div className="loading">{t('materials.lots.loading', 'Loading lots...')}</div>;
    }

    if (error) {
      return <div className="error-message">{error}</div>;
    }

    if (lots.length === 0) {
      return (
        <div className="info-message" style={{ textAlign: 'center', padding: '24px' }}>
          {t('materials.lots.noLots', 'No lots found for this item.')}
        </div>
      );
    }

    return (
      <div className="transactions-table">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('materials.lots.lotNumber', 'Lot Number')}</th>
              <th>{t('materials.lots.provider', 'Provider')}</th>
              <th>{t('materials.lots.poNumber', 'PO #')}</th>
              <th>{t('materials.lots.status', 'Status')}</th>
              <th>{t('materials.lots.receivedDate', 'Received Date')}</th>
              <th style={{ textAlign: 'right' }}>{t('materials.lots.initialQty', 'Initial Qty')}</th>
              <th style={{ textAlign: 'right' }}>{t('materials.lots.currentQty', 'Current Qty')}</th>
              <th>{t('materials.lots.location', 'Location')}</th>
              <th>{t('materials.lots.expiryDate', 'Expiry')}</th>
              <th style={{ textAlign: 'right' }}>{t('materials.lots.unitCost', 'Unit Cost')}</th>
            </tr>
          </thead>
          <tbody>
            {lots.map((lot) => (
              <tr
                key={lot.lot_id}
                onClick={() => handleRowClick(lot)}
                style={{ cursor: 'pointer' }}
              >
                <td style={{ fontWeight: 500 }}>{lot.lot_number}</td>
                <td>{lot.provider_name || '-'}</td>
                <td>{lot.po_number || '-'}</td>
                <td>{renderStatusBadge(lot.status)}</td>
                <td>{formatDate(lot.received_date)}</td>
                <td style={{ textAlign: 'right' }}>{formatQuantity(lot.initial_quantity)}</td>
                <td style={{ textAlign: 'right' }}>{formatQuantity(lot.current_quantity)}</td>
                <td>{lot.location || '-'}</td>
                <td>{formatDate(lot.expiry_date)}</td>
                <td style={{ textAlign: 'right' }}>{formatCost(lot.unit_cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderDetailModal = () => {
    if (!selectedLot) return null;

    return (
      <div className="modal-overlay" onClick={handleCloseModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
          <div className="modal-header">
            <h2>{t('materials.lots.lotDetail', 'Lot Detail')} - {selectedLot.lot_number}</h2>
            <button className="modal-close" onClick={handleCloseModal}>&times;</button>
          </div>

          {detailLoading ? (
            <div className="modal-body">
              <div className="loading">{t('materials.lots.loading', 'Loading...')}</div>
            </div>
          ) : (
            <>
              <div className="modal-body">
                {/* Read-only info */}
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('materials.lots.lotNumber', 'Lot Number')}</label>
                    <input type="text" value={selectedLot.lot_number || ''} disabled />
                  </div>
                  <div className="form-group">
                    <label>{t('materials.lots.status', 'Status')}</label>
                    <div style={{ padding: '8px 0' }}>{renderStatusBadge(selectedLot.status)}</div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('materials.lots.provider', 'Provider')}</label>
                    <input type="text" value={selectedLot.provider_name || '-'} disabled />
                  </div>
                  <div className="form-group">
                    <label>{t('materials.lots.poNumber', 'PO #')}</label>
                    <input type="text" value={selectedLot.po_number || '-'} disabled />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('materials.lots.grnNumber', 'GRN #')}</label>
                    <input type="text" value={selectedLot.grn_number || '-'} disabled />
                  </div>
                  <div className="form-group">
                    <label>{t('materials.lots.receivedDate', 'Received Date')}</label>
                    <input type="text" value={formatDate(selectedLot.received_date)} disabled />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('materials.lots.initialQty', 'Initial Quantity')}</label>
                    <input type="text" value={formatQuantity(selectedLot.initial_quantity)} disabled />
                  </div>
                  <div className="form-group">
                    <label>{t('materials.lots.currentQty', 'Current Quantity')}</label>
                    <input type="text" value={formatQuantity(selectedLot.current_quantity)} disabled />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('materials.lots.unitCost', 'Unit Cost')}</label>
                    <input type="text" value={formatCost(selectedLot.unit_cost)} disabled />
                  </div>
                  <div className="form-group">
                    <label>{t('materials.lots.createdDate', 'Created Date')}</label>
                    <input type="text" value={formatDate(selectedLot.created_date)} disabled />
                  </div>
                </div>

                <hr style={{ margin: '16px 0', borderColor: '#e0e0e0' }} />

                {/* Editable fields */}
                <h3 style={{ marginBottom: '12px', fontSize: '0.95em', color: '#555' }}>
                  {t('materials.lots.editableFields', 'Editable Fields')}
                </h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('materials.lots.location', 'Location')}</label>
                    <input
                      type="text"
                      value={editData.location || ''}
                      onChange={(e) => handleEditChange('location', e.target.value)}
                      disabled={!editMode}
                      style={!editMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('materials.lots.supplierBatchNumber', 'Supplier Batch #')}</label>
                    <input
                      type="text"
                      value={editData.supplier_batch_number || ''}
                      onChange={(e) => handleEditChange('supplier_batch_number', e.target.value)}
                      disabled={!editMode}
                      style={!editMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('materials.lots.expiryDate', 'Expiry Date')}</label>
                  <input
                    type="date"
                    value={editData.expiry_date || ''}
                    onChange={(e) => handleEditChange('expiry_date', e.target.value)}
                    disabled={!editMode}
                    style={!editMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                  />
                </div>

                <div className="form-group">
                  <label>{t('materials.lots.notes', 'Notes')}</label>
                  <textarea
                    value={editData.notes || ''}
                    onChange={(e) => handleEditChange('notes', e.target.value)}
                    rows="3"
                    disabled={!editMode}
                    style={{
                      width: '100%',
                      ...(! editMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {})
                    }}
                  />
                </div>
              </div>

              <div className="modal-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                {!editMode ? (
                  <>
                    <button className="btn-secondary" onClick={handleCloseModal}>
                      {t('materials.lots.close', 'Close')}
                    </button>
                    <button className="btn-primary" onClick={() => setEditMode(true)}>
                      {t('materials.lots.edit', 'Edit')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        setEditMode(false);
                        setEditData({
                          location: selectedLot.location || '',
                          notes: selectedLot.notes || '',
                          expiry_date: selectedLot.expiry_date ? selectedLot.expiry_date.substring(0, 10) : '',
                          supplier_batch_number: selectedLot.supplier_batch_number || ''
                        });
                      }}
                    >
                      {t('materials.lots.cancel', 'Cancel')}
                    </button>
                    <button
                      className="btn-primary"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving
                        ? t('materials.lots.saving', 'Saving...')
                        : t('materials.lots.save', 'Save')}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('materials.lots.title', 'Lots / Batches')}</h3>
        {renderFilters()}
        {renderTable()}
      </div>

      {renderDetailModal()}
    </div>
  );
};

export default ItemLotsTab;
